import { useQuery } from '@tanstack/react-query'
import { api } from './api'

export type MetricRange = '1h' | '6h' | '24h' | '7d' | '30d'

export interface NodeMetricPoint {
  b: string
  cpu_pct: number | null
  load1: number | null
  mem_used: number | null
  mem_total: number | null
  disk_used: number | null
  disk_total: number | null
  net_rx_bps: number | null
  net_tx_bps: number | null
}

export interface MetricsResponse<P> {
  range: MetricRange
  bucket_seconds: number
  points: P[]
}

export interface ServiceMetricPoint {
  b: string
  cpu_pct: number | null
  mem_used: number | null
  mem_limit: number | null
  net_rx_bps: number | null
  net_tx_bps: number | null
}

export function useServiceMetrics(serviceId: string | undefined) {
  return useQuery<MetricsResponse<ServiceMetricPoint>>({
    queryKey: ['metrics', 'services', serviceId, '1h'],
    queryFn: () => api<MetricsResponse<ServiceMetricPoint>>(`/api/metrics/services/${serviceId}/containers?range=1h`),
    enabled: !!serviceId,
    refetchInterval: 15_000,
  })
}

export function useNodeMetrics(nodeId: string | undefined, range: MetricRange) {
  return useQuery<MetricsResponse<NodeMetricPoint>>({
    queryKey: ['metrics', 'nodes', nodeId, range],
    queryFn: () =>
      api<MetricsResponse<NodeMetricPoint>>(
        `/api/metrics/nodes/${nodeId}?range=${range}`,
      ),
    enabled: !!nodeId,
    refetchInterval: range === '1h' ? 15_000 : 60_000,
  })
}

export type ContainerOrderBy = 'cpu' | 'mem' | 'net_rx' | 'net_tx' | 'restart_count'

export interface NodeTopContainer {
  container_id: string
  container_name: string | null
  service_id: string | null
  service_name: string | null
  stack: string | null
  avg_cpu_pct: number | null
  max_cpu_pct: number | null
  avg_mem_used: number | null
  max_mem_used: number | null
  max_mem_limit: number | null
  avg_net_rx_bps: number | null
  avg_net_tx_bps: number | null
  avg_block_read_bps: number | null
  avg_block_write_bps: number | null
  max_restart_count: number | null
  last_seen: string | null
  status: string | null
}

export interface NodeTopContainersResponse {
  range: MetricRange
  order_by: ContainerOrderBy
  limit: number
  containers: NodeTopContainer[]
}

export function useNodeTopContainers(
  nodeId: string | undefined,
  range: MetricRange,
  orderBy: ContainerOrderBy,
  limit = 15,
) {
  return useQuery<NodeTopContainersResponse>({
    queryKey: ['metrics', 'nodes', nodeId, 'containers', 'top', range, orderBy, limit],
    queryFn: () =>
      api<NodeTopContainersResponse>(
        `/api/metrics/nodes/${nodeId}/containers/top?range=${range}&order_by=${orderBy}&limit=${limit}`,
      ),
    enabled: !!nodeId,
    refetchInterval: range === '1h' ? 15_000 : 60_000,
  })
}
